/**
 * Documentation Browser Page
 *
 * A full-page documentation browser with two modes:
 * 1. Object Reference - Browse IDD objects by group
 * 2. Engineering Reference - Browse engineering documentation by category
 *
 * Features:
 * - Keyboard shortcuts (/ to search, Backspace to go back)
 * - Navigation history with back button
 * - Object list filtering
 * - Sticky TOC for engineering docs
 * - Popular objects in empty state
 * - Improved mobile UX with stacked selectors
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDocsStore } from '../store/docsStore';
import { useIDDStore, useSchemaGroups, useSchemaIsLoaded } from '@/stores/iddStore';
import { DocsSearch } from './DocsSearch';
import { ObjectReferenceContent } from './ObjectReferenceContent';
import { MarkdownRenderer, extractToc, type TocEntry } from '../services/markdownRenderer';
import {
  getEngineeringDocs,
  getEngineeringCategories,
  getEngineeringDocsByCategory,
  getEngineeringDoc,
} from '../services/engineeringReference';
import {
  getInputOutputDocs,
  getInputOutputCategories,
  getInputOutputDocsByCategory,
  getInputOutputDoc,
} from '../services/inputOutputReference';
import type { EngineeringDocument } from '../types';
import { cn } from '@/lib/utils';
import { ResizablePanel } from '@/components/ResizablePanel';
import {
  FolderIcon,
  FileIcon,
  BookIcon,
  ChevronRightIcon,
  ListIcon,
  SearchIcon,
  ArrowLeftIcon,
  SparklesIcon,
  LinkIcon,
} from './icons';

// Popular objects for empty state (commonly used EnergyPlus objects)
const POPULAR_OBJECTS = [
  { name: 'Zone', description: 'Define thermal zones' },
  { name: 'Building', description: 'Building-level parameters' },
  { name: 'Material', description: 'Material properties' },
  { name: 'Construction', description: 'Layer assemblies' },
  { name: 'Schedule:Compact', description: 'Time-based schedules' },
  { name: 'People', description: 'Occupancy loads' },
  { name: 'Lights', description: 'Lighting loads' },
  { name: 'ElectricEquipment', description: 'Plug loads' },
];

// Navigation history entry
interface HistoryEntry {
  mode: BrowseMode;
  group?: string | undefined;
  object?: string | undefined;
  category?: string | undefined;
  engDoc?: EngineeringDocument | undefined;
}

/**
 * Loading skeleton for content area
 */
function ContentSkeleton() {
  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 animate-pulse overflow-x-hidden">
      {/* Breadcrumb skeleton */}
      <div className="flex items-center gap-2 mb-4">
        <div className="h-4 w-32 bg-muted rounded" />
        <div className="h-4 w-4 bg-muted rounded" />
        <div className="h-4 w-24 bg-muted rounded" />
      </div>

      {/* Title skeleton */}
      <div className="h-7 w-48 bg-muted rounded mb-4" />

      {/* Description skeleton */}
      <div className="space-y-2 mb-6">
        <div className="h-4 w-full bg-muted rounded" />
        <div className="h-4 w-3/4 bg-muted rounded" />
      </div>

      {/* Fields skeleton */}
      <div className="h-5 w-24 bg-muted rounded mb-3" />
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="border rounded-lg p-3">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 bg-muted rounded" />
              <div className="h-4 w-6 bg-muted rounded" />
              <div className="h-4 flex-1 bg-muted rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Sticky Table of Contents for engineering reference pages
 */
function StickyToc({
  entries,
  activeId,
  className,
}: {
  entries: TocEntry[];
  activeId: string | null;
  className?: string;
}) {
  if (entries.length === 0) return null;

  const handleClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <nav className={cn('space-y-1', className)}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
        On this page
      </h4>
      <ul className="space-y-0.5">
        {entries.map((entry) => (
          <li key={entry.id} style={{ paddingLeft: `${(entry.level - 1) * 0.5}rem` }}>
            <button
              onClick={() => handleClick(entry.id)}
              className={cn(
                'text-left text-xs py-1 hover:text-primary transition-colors w-full truncate block',
                activeId === entry.id
                  ? 'text-primary font-medium'
                  : entry.level === 1
                    ? 'text-foreground/80'
                    : 'text-muted-foreground'
              )}
              title={entry.text}
            >
              {entry.text}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * Mini Table of Contents (collapsible, inline)
 */
function MiniToc({ entries, className }: { entries: TocEntry[]; className?: string }) {
  const [isExpanded, setIsExpanded] = useState(true);

  if (entries.length === 0) return null;

  const handleClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className={cn('border rounded-lg bg-muted/30 mb-6 lg:hidden', className)}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
      >
        <span className="flex items-center gap-2">
          <ListIcon className="h-4 w-4" />
          Table of Contents
        </span>
        <ChevronRightIcon
          className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-90')}
        />
      </button>
      {isExpanded && (
        <nav className="px-3 pb-3">
          <ul className="space-y-1">
            {entries.map((entry) => (
              <li key={entry.id} style={{ paddingLeft: `${(entry.level - 1) * 0.75}rem` }}>
                <button
                  onClick={() => handleClick(entry.id)}
                  className={cn(
                    'text-left text-sm py-1 hover:text-primary transition-colors w-full truncate',
                    entry.level === 1 && 'font-medium text-foreground',
                    entry.level === 2 && 'text-foreground/80',
                    entry.level === 3 && 'text-muted-foreground'
                  )}
                  title={entry.text}
                >
                  {entry.text}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}

type BrowseMode = 'objects' | 'engineering' | 'input-output';

/**
 * Engineering document view with TOC
 */
function EngineeringDocView({
  doc,
  onObjectClick,
  onCategoryClick,
}: {
  doc: EngineeringDocument;
  onObjectClick: (objectType: string) => void;
  onCategoryClick: (category: string) => void;
}) {
  const tocEntries = useMemo(() => extractToc(doc.content), [doc.content]);
  const [activeHeadingId, setActiveHeadingId] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Track active heading on scroll
  useEffect(() => {
    if (tocEntries.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveHeadingId(entry.target.id);
            break;
          }
        }
      },
      { rootMargin: '-80px 0px -80% 0px' }
    );

    const headings = contentRef.current?.querySelectorAll('h1[id], h2[id], h3[id]');
    headings?.forEach((heading) => observer.observe(heading));

    return () => observer.disconnect();
  }, [tocEntries, doc.id]);

  return (
    <div className="flex">
      {/* Main content */}
      <div className="flex-1 min-w-0 max-w-4xl mx-auto p-4 sm:p-6 overflow-x-hidden" ref={contentRef}>
        {/* Clickable Breadcrumb */}
        <div className="flex items-center gap-1 text-sm text-muted-foreground mb-4 min-w-0">
          <button
            onClick={() => onCategoryClick(doc.category)}
            className="shrink-0 hover:text-foreground transition-colors truncate max-w-[40%]"
          >
            {doc.category}
          </button>
          <ChevronRightIcon className="h-4 w-4 shrink-0" />
          <span className="text-foreground font-medium truncate">{doc.title}</span>
        </div>

        {/* Mini TOC for mobile/tablet */}
        {tocEntries.length > 2 && <MiniToc entries={tocEntries} />}

        {/* Content */}
        <MarkdownRenderer content={doc.content} onObjectClick={onObjectClick} />
      </div>

      {/* Sticky TOC sidebar for desktop */}
      {tocEntries.length > 2 && (
        <div className="hidden lg:block w-56 shrink-0">
          <div className="sticky top-4 p-4 max-h-[calc(100vh-2rem)] overflow-y-auto">
            <StickyToc entries={tocEntries} activeId={activeHeadingId} />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Empty state with popular objects and guidance
 */
function EmptyState({
  mode,
  onObjectSelect,
  recentObjects,
}: {
  mode: BrowseMode;
  onObjectSelect: (objectType: string) => void;
  recentObjects: string[];
}) {
  return (
    <div className="flex items-center justify-center min-h-[60vh] md:h-full py-8 md:py-0">
      <div className="text-center max-w-md px-4">
        <BookIcon className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
        <h3 className="text-lg font-medium text-foreground mb-2">
          {mode === 'objects'
            ? 'EnergyPlus Object Reference'
            : mode === 'input-output'
              ? 'Input/Output Reference'
              : 'Engineering Reference'}
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          {mode === 'objects'
            ? 'Browse detailed documentation for all EnergyPlus objects, fields, and their properties.'
            : mode === 'input-output'
              ? 'Explore input and output descriptions for EnergyPlus objects, including field details and examples.'
              : 'Explore in-depth engineering documentation covering algorithms, calculations, and technical details.'}
        </p>

        {/* Keyboard shortcut hint */}
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground mb-6">
          <kbd className="px-2 py-1 bg-muted rounded border text-foreground font-mono">/</kbd>
          <span>to search</span>
        </div>

        {mode === 'objects' && (
          <>
            {/* Recent objects */}
            {recentObjects.length > 0 && (
              <div className="mb-6">
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3 flex items-center justify-center gap-1">
                  <ClockIcon className="h-3 w-3" />
                  Recently Viewed
                </h4>
                <div className="flex flex-wrap justify-center gap-2">
                  {recentObjects.slice(0, 5).map((obj) => (
                    <button
                      key={obj}
                      onClick={() => onObjectSelect(obj)}
                      className="text-sm px-3 py-1.5 rounded-md bg-muted hover:bg-primary/10 hover:text-primary transition-colors"
                    >
                      {obj}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Popular objects */}
            <div>
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3 flex items-center justify-center gap-1">
                <SparklesIcon className="h-3 w-3" />
                Popular Objects
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {POPULAR_OBJECTS.map((obj) => (
                  <button
                    key={obj.name}
                    onClick={() => onObjectSelect(obj.name)}
                    className="text-left p-2 rounded-md bg-muted/50 hover:bg-primary/10 hover:text-primary transition-colors group"
                  >
                    <div className="text-sm font-medium">{obj.name}</div>
                    <div className="text-xs text-muted-foreground group-hover:text-primary/70">
                      {obj.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Clock icon for recent items
function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

export function DocsBrowser() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [browseMode, setBrowseMode] = useState<BrowseMode>('objects');
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedObject, setSelectedObject] = useState<string | null>(null);
  const [objectListFilter, setObjectListFilter] = useState('');
  const [isContentLoading, setIsContentLoading] = useState(false);

  // Engineering reference state
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedEngDoc, setSelectedEngDoc] = useState<EngineeringDocument | null>(null);

  // Input-Output reference state
  const [selectedIOCategory, setSelectedIOCategory] = useState<string | null>(null);
  const [selectedIODoc, setSelectedIODoc] = useState<EngineeringDocument | null>(null);

  // Navigation history
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [recentObjects, setRecentObjects] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('docs-recent-objects');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Track if we've processed URL params
  const hasProcessedUrlParams = useRef(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const groups = useSchemaGroups();
  const isSchemaLoaded = useSchemaIsLoaded();
  const schema = useIDDStore((state) => state.schema);
  const isLoading = useIDDStore((state) => state.isLoading);
  const loadSchemaFromUrl = useIDDStore((state) => state.loadSchemaFromUrl);
  const getObjectDocumentation = useDocsStore((state) => state.getObjectDocumentation);
  const initializeSearch = useDocsStore((state) => state.initializeSearch);
  const isSearchReady = useDocsStore((state) => state.isSearchReady);

  // Ref to track if we've already initiated schema loading
  const hasInitiatedLoading = useRef(false);

  // Engineering reference data
  const engineeringCategories = useMemo(() => getEngineeringCategories(), []);
  const engineeringDocsInCategory = useMemo(() => {
    if (!selectedCategory) return [];
    return getEngineeringDocsByCategory(selectedCategory);
  }, [selectedCategory]);
  const allEngineeringDocs = useMemo(() => getEngineeringDocs(), []);

  // Input-Output reference data
  const ioCategories = useMemo(() => getInputOutputCategories(), []);
  const ioDocsInCategory = useMemo(() => {
    if (!selectedIOCategory) return [];
    return getInputOutputDocsByCategory(selectedIOCategory);
  }, [selectedIOCategory]);
  const allIODocs = useMemo(() => getInputOutputDocs(), []);

  // Save recent objects to localStorage
  useEffect(() => {
    if (recentObjects.length > 0) {
      localStorage.setItem('docs-recent-objects', JSON.stringify(recentObjects));
    }
  }, [recentObjects]);

  // Add to recent objects when selecting
  const addToRecentObjects = useCallback((objectType: string) => {
    setRecentObjects((prev) => {
      const filtered = prev.filter((o) => o !== objectType);
      return [objectType, ...filtered].slice(0, 10);
    });
  }, []);

  // Push current state to history before navigation
  const pushToHistory = useCallback(() => {
    const entry: HistoryEntry = {
      mode: browseMode,
      group: selectedGroup || undefined,
      object: selectedObject || undefined,
      category: selectedCategory || undefined,
      engDoc: selectedEngDoc || undefined,
    };
    setHistory((prev) => [...prev, entry]);
  }, [browseMode, selectedGroup, selectedObject, selectedCategory, selectedEngDoc]);

  // Go back in history
  const goBack = useCallback(() => {
    if (history.length === 0) return;

    const prev = history[history.length - 1];
    if (!prev) return;

    setHistory((h) => h.slice(0, -1));
    setBrowseMode(prev.mode);
    setSelectedGroup(prev.group || null);
    setSelectedObject(prev.object || null);
    setSelectedCategory(prev.category || null);
    setSelectedEngDoc(prev.engDoc || null);
  }, [history]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        // Allow Escape to blur from search
        if (e.key === 'Escape') {
          (e.target as HTMLElement).blur();
        }
        return;
      }

      // "/" to focus search
      if (e.key === '/' || (e.key === 'k' && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }

      // Backspace to go back
      if (e.key === 'Backspace' && history.length > 0) {
        e.preventDefault();
        goBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [history.length, goBack]);

  // Process URL parameters on mount (e.g., /docs?object=Zone or /docs?doc=coils-doc-id&section=heading)
  useEffect(() => {
    if (hasProcessedUrlParams.current) return;

    const objectParam = searchParams.get('object');
    const docParam = searchParams.get('doc');
    const sectionParam = searchParams.get('section');

    if (objectParam) {
      hasProcessedUrlParams.current = true;
      setBrowseMode('objects');
      setSelectedObject(objectParam);
      addToRecentObjects(objectParam);

      if (isSchemaLoaded) {
        const objDef = schema.objectTypes.get(objectParam.toLowerCase());
        if (objDef) {
          setSelectedGroup(objDef.group);
        }
      }
      setSearchParams({}, { replace: true });
    } else if (docParam) {
      // Handle engineering or I/O doc link: /docs?doc=some-doc-id&section=section-slug
      hasProcessedUrlParams.current = true;

      // First try engineering docs
      const engDoc = getEngineeringDoc(docParam);
      if (engDoc) {
        setBrowseMode('engineering');
        setSelectedCategory(engDoc.category);
        setSelectedEngDoc(engDoc);
      } else {
        // Try I/O docs
        const ioDoc = getInputOutputDoc(docParam);
        if (ioDoc) {
          setBrowseMode('input-output');
          setSelectedIOCategory(ioDoc.category);
          setSelectedIODoc(ioDoc);
        }
      }

      // Scroll to section after a short delay to allow rendering
      if (sectionParam) {
        setTimeout(() => {
          const element = document.getElementById(sectionParam);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, isSchemaLoaded, schema, addToRecentObjects]);

  // Load schema on mount if not already loaded
  useEffect(() => {
    const schemaIsPopulated = schema.objectTypes.size > 0;
    if (!schemaIsPopulated && !isLoading && !hasInitiatedLoading.current) {
      hasInitiatedLoading.current = true;
      loadSchemaFromUrl('/energyplus/Energy+.idd');
    }
  }, [schema.objectTypes.size, isLoading, loadSchemaFromUrl]);

  // Initialize search when schema is loaded
  useEffect(() => {
    if (isSchemaLoaded && !isSearchReady) {
      initializeSearch(schema);
    }
  }, [isSchemaLoaded, isSearchReady, schema, initializeSearch]);

  // Select first group/category by default
  useEffect(() => {
    if (browseMode === 'objects' && groups.length > 0 && !selectedGroup && groups[0]) {
      setSelectedGroup(groups[0].name);
    }
    if (
      browseMode === 'engineering' &&
      engineeringCategories.length > 0 &&
      !selectedCategory &&
      engineeringCategories[0]
    ) {
      setSelectedCategory(engineeringCategories[0].name);
    }
    if (browseMode === 'input-output' && ioCategories.length > 0 && !selectedIOCategory && ioCategories[0]) {
      setSelectedIOCategory(ioCategories[0].name);
    }
  }, [groups, selectedGroup, browseMode, engineeringCategories, selectedCategory, ioCategories, selectedIOCategory]);

  // Filter objects in group
  const objectsInGroup = useMemo(() => {
    if (!selectedGroup) return [];
    const group = groups.find((g) => g.name === selectedGroup);
    const objects = group?.objectTypes || [];

    if (!objectListFilter.trim()) return objects;

    const filter = objectListFilter.toLowerCase();
    return objects.filter((name) => name.toLowerCase().includes(filter));
  }, [selectedGroup, groups, objectListFilter]);

  const currentDocs = useMemo(() => {
    if (!selectedObject || !isSchemaLoaded) return null;
    return getObjectDocumentation(selectedObject, schema);
  }, [selectedObject, schema, isSchemaLoaded, getObjectDocumentation]);

  const handleSearchSelect = (result: { objectType: string }) => {
    pushToHistory();

    const engDoc = allEngineeringDocs.find(
      (d) => d.id === result.objectType || d.title === result.objectType
    );
    if (engDoc) {
      setBrowseMode('engineering');
      setSelectedCategory(engDoc.category);
      setSelectedEngDoc(engDoc);
      return;
    }

    // Check if this is an I/O doc
    const ioDoc = allIODocs.find(
      (d) => d.id === result.objectType || d.title === result.objectType
    );
    if (ioDoc) {
      setBrowseMode('input-output');
      setSelectedIOCategory(ioDoc.category);
      setSelectedIODoc(ioDoc);
      return;
    }

    // Otherwise it's an object
    setBrowseMode('objects');
    setSelectedObject(result.objectType);
    addToRecentObjects(result.objectType);
    const objDef = schema.objectTypes.get(result.objectType.toLowerCase());
    if (objDef) {
      setSelectedGroup(objDef.group);
    }
  };

  const handleGroupSelect = (groupName: string) => {
    if (selectedObject) pushToHistory();
    setSelectedGroup(groupName);
    setSelectedObject(null);
    setObjectListFilter('');
  };

  const handleObjectSelect = (objectType: string) => {
    if (selectedObject && selectedObject !== objectType) pushToHistory();
    setIsContentLoading(true);
    setSelectedObject(objectType);
    addToRecentObjects(objectType);
    // Simulate brief loading for smooth transition
    setTimeout(() => setIsContentLoading(false), 100);
  };

  const handleCategorySelect = (categoryName: string) => {
    if (selectedEngDoc) pushToHistory();
    setSelectedCategory(categoryName);
    setSelectedEngDoc(null);
  };

  const handleEngDocSelect = (doc: EngineeringDocument) => {
    if (selectedEngDoc && selectedEngDoc.id !== doc.id) pushToHistory();
    setIsContentLoading(true);
    setSelectedEngDoc(doc);
    setTimeout(() => setIsContentLoading(false), 100);
  };

  const handleIOCategorySelect = (categoryName: string) => {
    setSelectedIOCategory(categoryName);
    setSelectedIODoc(null);
  };

  const handleIODocSelect = (doc: EngineeringDocument) => {
    setSelectedIODoc(doc);
  };

  const handleModeChange = (mode: BrowseMode) => {
    if (mode !== browseMode) {
      pushToHistory();
    }
    setBrowseMode(mode);
    if (mode === 'objects') {
      setSelectedEngDoc(null);
      setSelectedIODoc(null);
    } else if (mode === 'engineering') {
      setSelectedObject(null);
      setSelectedIODoc(null);
    } else {
      setSelectedObject(null);
      setSelectedEngDoc(null);
    }
  };

  if (!isSchemaLoaded) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading EnergyPlus schema...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row h-full">
      {/* Sidebar */}
      <ResizablePanel
        side="left"
        defaultWidth={256}
        minWidth={180}
        maxWidth={350}
        storageKey="docs-browser-left-panel"
        className="bg-muted/30 overflow-y-auto"
        hideOnMobile={true}
        mobileBreakpoint="md"
        resizeAriaLabel="Resize documentation sidebar"
      >
        <div className="p-4">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <BookIcon className="h-5 w-5" />
            Documentation
          </h2>

          <DocsSearch
            ref={searchInputRef}
            onSelect={handleSearchSelect}
            placeholder="Search all docs..."
            className="mb-4"
          />

          {/* Mode Toggle */}
          <div className="flex rounded-lg bg-muted p-1 mb-4">
            <button
              onClick={() => handleModeChange('objects')}
              className={cn(
                'flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors',
                browseMode === 'objects'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Objects
            </button>
            <button
              onClick={() => handleModeChange('input-output')}
              className={cn(
                'flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors',
                browseMode === 'input-output'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              I/O Ref
            </button>
            <button
              onClick={() => handleModeChange('engineering')}
              className={cn(
                'flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors',
                browseMode === 'engineering'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Eng Ref
            </button>
          </div>

          <nav className="space-y-1">
            {browseMode === 'objects' && (
              <>
                <h3 className="text-xs font-medium text-muted-foreground px-2 py-1 uppercase tracking-wider">
                  Object Groups
                </h3>
                {groups.map((group) => (
                  <button
                    key={group.name}
                    onClick={() => handleGroupSelect(group.name)}
                    className={cn(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left',
                      'transition-colors',
                      selectedGroup === group.name
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-foreground/80 hover:bg-muted'
                    )}
                  >
                    <FolderIcon className="h-4 w-4 shrink-0" />
                    <span className="truncate flex-1">{group.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {group.objectTypes.length}
                    </span>
                  </button>
                ))}
              </>
            )}
            {browseMode === 'engineering' && (
              <>
                <h3 className="text-xs font-medium text-muted-foreground px-2 py-1 uppercase tracking-wider">
                  Engineering Categories
                </h3>
                {engineeringCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategorySelect(cat.name)}
                    className={cn(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left',
                      'transition-colors',
                      selectedCategory === cat.name
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-foreground/80 hover:bg-muted'
                    )}
                  >
                    <FolderIcon className="h-4 w-4 shrink-0" />
                    <span className="truncate flex-1">{cat.name}</span>
                  </button>
                ))}
              </>
            )}
            {browseMode === 'input-output' && (
              <>
                <h3 className="text-xs font-medium text-muted-foreground px-2 py-1 uppercase tracking-wider">
                  I/O Reference Categories
                </h3>
                {ioCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleIOCategorySelect(cat.name)}
                    className={cn(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left',
                      'transition-colors',
                      selectedIOCategory === cat.name
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-foreground/80 hover:bg-muted'
                    )}
                  >
                    <FolderIcon className="h-4 w-4 shrink-0" />
                    <span className="truncate flex-1">{cat.name}</span>
                  </button>
                ))}
              </>
            )}
          </nav>
        </div>
      </ResizablePanel>

      {/* Middle list */}
      <ResizablePanel
        side="left"
        defaultWidth={288}
        minWidth={200}
        maxWidth={400}
        storageKey="docs-browser-middle-panel"
        className="overflow-y-auto"
        hideOnMobile={true}
        mobileBreakpoint="lg"
        resizeAriaLabel="Resize object list panel"
      >
        <div className="p-4">
          {browseMode === 'objects' && (
            <>
              <h3 className="font-medium mb-3 flex items-center gap-2">
                {selectedGroup || 'Objects'}
                {objectsInGroup.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    ({objectsInGroup.length}
                    {objectListFilter && ` of ${groups.find((g) => g.name === selectedGroup)?.objectTypes.length || 0}`})
                  </span>
                )}
              </h3>

              {/* Object list filter */}
              <div className="relative mb-3">
                <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  type="text"
                  value={objectListFilter}
                  onChange={(e) => setObjectListFilter(e.target.value)}
                  placeholder="Filter objects..."
                  className={cn(
                    'w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-input',
                    'bg-background text-foreground placeholder:text-muted-foreground',
                    'focus:outline-none focus:ring-1 focus:ring-ring'
                  )}
                />
              </div>

              <div className="space-y-0.5">
                {objectsInGroup.map((objName) => (
                  <button
                    key={objName}
                    onClick={() => handleObjectSelect(objName)}
                    className={cn(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left',
                      'transition-colors',
                      selectedObject === objName
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-foreground/80 hover:bg-muted'
                    )}
                  >
                    <FileIcon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{objName}</span>
                  </button>
                ))}
                {objectsInGroup.length === 0 && objectListFilter && (
                  <p className="text-sm text-muted-foreground px-2 py-4 text-center">
                    No objects match "{objectListFilter}"
                  </p>
                )}
              </div>
            </>
          )}
          {browseMode === 'engineering' && (
            <>
              <h3 className="font-medium mb-3 flex items-center gap-2">
                {selectedCategory || 'Documents'}
                {engineeringDocsInCategory.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    ({engineeringDocsInCategory.length})
                  </span>
                )}
              </h3>

              <div className="space-y-0.5">
                {engineeringDocsInCategory.map((doc) => (
                  <button
                    key={doc.id}
                    onClick={() => handleEngDocSelect(doc)}
                    className={cn(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left',
                      'transition-colors',
                      selectedEngDoc?.id === doc.id
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-foreground/80 hover:bg-muted'
                    )}
                  >
                    <FileIcon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{doc.title}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          {browseMode === 'input-output' && (
            <>
              <h3 className="font-medium mb-3 flex items-center gap-2">
                {selectedIOCategory || 'Documents'}
                {ioDocsInCategory.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    ({ioDocsInCategory.length})
                  </span>
                )}
              </h3>

              <div className="space-y-0.5">
                {ioDocsInCategory.map((doc) => (
                  <button
                    key={doc.id}
                    onClick={() => handleIODocSelect(doc)}
                    className={cn(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left',
                      'transition-colors',
                      selectedIODoc?.id === doc.id
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-foreground/80 hover:bg-muted'
                    )}
                  >
                    <FileIcon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{doc.title}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </ResizablePanel>

      {/* Mobile navigation */}
      <div className="md:hidden flex flex-col shrink-0">
        {/* Mobile search */}
        <div className="p-3 border-b bg-background sticky top-0 z-10">
          <DocsSearch onSelect={handleSearchSelect} placeholder="Search documentation..." />
        </div>

        {/* Mobile mode toggle */}
        <div className="flex rounded-lg bg-muted p-1 mx-3 mt-3">
          <button
            onClick={() => handleModeChange('objects')}
            className={cn(
              'flex-1 px-2 py-2 text-sm font-medium rounded-md transition-colors',
              browseMode === 'objects'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Objects
          </button>
          <button
            onClick={() => handleModeChange('input-output')}
            className={cn(
              'flex-1 px-2 py-2 text-sm font-medium rounded-md transition-colors',
              browseMode === 'input-output'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            I/O Ref
          </button>
          <button
            onClick={() => handleModeChange('engineering')}
            className={cn(
              'flex-1 px-2 py-2 text-sm font-medium rounded-md transition-colors',
              browseMode === 'engineering'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Eng Ref
          </button>
        </div>

        {/* Mobile selectors - stacked for better touch targets */}
        <div className="flex flex-col border-b bg-background mt-3">
          {browseMode === 'objects' && (
            <>
              <select
                value={selectedGroup || ''}
                onChange={(e) => handleGroupSelect(e.target.value)}
                className="w-full p-3 text-sm bg-background border-b focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
                aria-label="Select object group"
              >
                <option value="">Select group</option>
                {groups.map((group) => (
                  <option key={group.name} value={group.name}>
                    {group.name} ({group.objectTypes.length})
                  </option>
                ))}
              </select>
              <select
                value={selectedObject || ''}
                onChange={(e) => handleObjectSelect(e.target.value)}
                className="w-full p-3 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
                aria-label="Select object"
              >
                <option value="">Select object</option>
                {objectsInGroup.map((objName) => (
                  <option key={objName} value={objName}>
                    {objName}
                  </option>
                ))}
              </select>
            </>
          )}
          {browseMode === 'engineering' && (
            <>
              <select
                value={selectedCategory || ''}
                onChange={(e) => handleCategorySelect(e.target.value)}
                className="w-full p-3 text-sm bg-background border-b focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
                aria-label="Select category"
              >
                <option value="">Select category</option>
                {engineeringCategories.map((cat) => (
                  <option key={cat.id} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <select
                value={selectedEngDoc?.id || ''}
                onChange={(e) => {
                  const doc = engineeringDocsInCategory.find((d) => d.id === e.target.value);
                  if (doc) handleEngDocSelect(doc);
                }}
                className="w-full p-3 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
                aria-label="Select document"
              >
                <option value="">Select document</option>
                {engineeringDocsInCategory.map((doc) => (
                  <option key={doc.id} value={doc.id}>
                    {doc.title}
                  </option>
                ))}
              </select>
            </>
          )}
          {browseMode === 'input-output' && (
            <>
              <select
                value={selectedIOCategory || ''}
                onChange={(e) => handleIOCategorySelect(e.target.value)}
                className="flex-1 p-3 text-sm bg-background border-r focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary touch-target"
                aria-label="Select I/O category"
              >
                <option value="">Select category</option>
                {ioCategories.map((cat) => (
                  <option key={cat.id} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <select
                value={selectedIODoc?.id || ''}
                onChange={(e) => {
                  const doc = ioDocsInCategory.find((d) => d.id === e.target.value);
                  if (doc) handleIODocSelect(doc);
                }}
                className="flex-1 p-3 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary touch-target"
                aria-label="Select I/O document"
              >
                <option value="">Select document</option>
                {ioDocsInCategory.map((doc) => (
                  <option key={doc.id} value={doc.id}>
                    {doc.title}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>

      {/* Documentation content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden min-w-0">
        {/* Back button */}
        {history.length > 0 && (
          <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b px-4 py-2">
            <button
              onClick={goBack}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back
              <span className="text-xs ml-1 opacity-60">({history.length})</span>
            </button>
          </div>
        )}

        {isContentLoading ? (
          <ContentSkeleton />
        ) : browseMode === 'objects' && currentDocs ? (
          <div className="max-w-4xl mx-auto p-4 sm:p-6 overflow-x-hidden">
            {/* Clickable Breadcrumb */}
            <div className="flex items-center gap-1 text-sm text-muted-foreground mb-4 min-w-0">
              <button
                onClick={() => handleGroupSelect(currentDocs.group)}
                className="shrink-0 hover:text-foreground transition-colors truncate max-w-[40%]"
              >
                {currentDocs.group}
              </button>
              <ChevronRightIcon className="h-4 w-4 shrink-0" />
              <span className="text-foreground font-medium truncate">{currentDocs.name}</span>
            </div>

            {/* Related objects - prominent position at top */}
            {currentDocs.relatedObjects.length > 0 && (
              <div className="mb-6 p-3 rounded-lg bg-muted/30 border">
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                  <LinkIcon className="h-3 w-3" />
                  Related Objects
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {currentDocs.relatedObjects.map((related) => (
                    <button
                      key={related}
                      onClick={() => handleObjectSelect(related)}
                      className="text-xs px-2 py-1 rounded bg-background hover:bg-primary/10 hover:text-primary border transition-colors"
                    >
                      {related}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Object Reference Content */}
            <ObjectReferenceContent
              objectType={currentDocs.name}
              memo={currentDocs.memo}
              fields={currentDocs.fields}
              isUnique={currentDocs.isUnique}
              isRequired={currentDocs.isRequired}
            />
          </div>
        ) : browseMode === 'engineering' && selectedEngDoc ? (
          <EngineeringDocView
            doc={selectedEngDoc}
            onObjectClick={(objectType) => {
              const objDef = schema.objectTypes.get(objectType.toLowerCase());
              if (objDef) {
                pushToHistory();
                setBrowseMode('objects');
                setSelectedGroup(objDef.group);
                setSelectedObject(objectType);
                addToRecentObjects(objectType);
              }
            }}
            onCategoryClick={(category) => {
              setSelectedEngDoc(null);
              setSelectedCategory(category);
            }}
          />
        ) : browseMode === 'input-output' && selectedIODoc ? (
          <EngineeringDocView
            doc={selectedIODoc}
            onObjectClick={(objectType) => {
              const objDef = schema.objectTypes.get(objectType.toLowerCase());
              if (objDef) {
                pushToHistory();
                setBrowseMode('objects');
                setSelectedGroup(objDef.group);
                setSelectedObject(objectType);
                addToRecentObjects(objectType);
              }
            }}
            onCategoryClick={(category) => {
              setSelectedIODoc(null);
              setSelectedIOCategory(category);
            }}
          />
        ) : (
          <EmptyState
            mode={browseMode}
            onObjectSelect={handleObjectSelect}
            recentObjects={recentObjects}
          />
        )}
      </div>
    </div>
  );
}
